import { useState } from 'react';
import { PaginationState } from '../../types';

/**
 * Hook for managing table pagination state
 *
 * This hook provides pagination functionality for tables, including
 * page navigation and rows per page configuration.
 *
 * @param initialState - Initial pagination configuration (page, rowsPerPage)
 *
 * @returns Object containing:
 *   - pagination: Current pagination state
 *   - onPageChange: Function to handle page changes
 *   - onRowsPerPageChange: Function to handle rows per page changes
 *   - setPagination: Function to set the entire pagination state
 *
 * @example
 * ```tsx
 * const { pagination, onPageChange, onRowsPerPageChange } = usePagination({
 *   page: 0,
 *   rowsPerPage: 10
 * });
 *
 * // Handle page change
 * onPageChange(null, 2); // Go to page 2
 *
 * // Handle rows per page change
 * const event = { target: { value: '25' } };
 * onRowsPerPageChange(event); // Set 25 rows per page
 *
 * // Current pagination info
 * console.log(pagination?.page, pagination?.rowsPerPage);
 * ```
 */
export const usePagination = (initialState?: PaginationState) => {
  const [pagination, setPagination] = useState(initialState);

  const onPageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => (prev ? { ...prev, page: newPage } : undefined));
  };

  const onRowsPerPageChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setPagination((prev) =>
      prev
        ? {
            ...prev,
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0,
          }
        : undefined,
    );
  };

  return { pagination, onPageChange, onRowsPerPageChange };
};
